<template>
  <div
    v-if="legendVisible"
    class="hex-legend"
    :class="{ 'is-collapsed': isCollapsed, 'is-stacked': stackWithTimeFilter }"
  >
    <button
      v-if="!screenshotMode"
      class="hex-legend-toggle"
      :title="isCollapsed ? $t('hexLegend.expandLegend') : $t('hexLegend.collapseLegend')"
      @click="toggleCollapsed"
    >
      {{ isCollapsed ? '+' : '-' }}
    </button>

    <div class="hex-legend-title">{{ $t('hexLegend.title') }}</div>

    <div v-show="!isCollapsed" class="hex-legend-scale">
        <div class="hex-legend-bar" :style="{ gridTemplateColumns: legendGridTemplate }">
          <div
            v-for="(stop, index) in stops"
            :key="index"
            class="hex-legend-stop"
            :style="{ background: stop.color }"
          />
        </div>
        
        <div class="hex-legend-labels" :style="{ gridTemplateColumns: legendGridTemplate }">
          <span 
            v-for="(tick, index) in boundaryTicks" 
            :key="index"
            class="tick"
            :class="{
              'is-first': index === 0,
              'is-last': index === boundaryTicks.length - 1,
            }"
            :style="getTickStyle(index)"
          >
            {{ tick }}
          </span>
        </div>

        <div class="hex-legend-unavailable">
            <div class="hex-legend-unavailable-swatch"></div>
            <span>{{ unavailableLabel }}</span>
        </div>

        <p v-if="gridCoverageNotice" class="hex-legend-coverage-note">
          {{ gridCoverageNotice }}
        </p>
    </div>

    <div v-if="!isCollapsed && mapViewMode === 'grid' && selectedCount > 0" class="hex-legend-selection">
      {{ $t('hexLegend.cellsSelected', { count: selectedCount }) }}
      <button class="hex-legend-clear" @click="clearSelection">{{ $t('hexLegend.clear') }}</button>
    </div>

    <div v-if="!isCollapsed && selectedPassId" class="hex-legend-selection">
      <div class="hex-legend-selected-copy">
        <span class="hex-legend-selected-name" :title="selectedPassLabel">{{ selectedPassLabel }}</span>
        <span v-if="selectedPassCloudLabel" class="hex-legend-selected-cloud">{{ selectedPassCloudLabel }}</span>
        <span v-if="selectedPassLatency != null" class="hex-legend-selected-cloud">
          {{ $t('popup.expectedLatency') }}: ~{{ Math.round(selectedPassLatency) }} min
        </span>
        <span v-if="selectedPassDelivery" class="hex-legend-selected-cloud">
          {{ $t('popup.expectedDelivery') }}: {{ formatDateTime(selectedPassDelivery) }}
        </span>
      </div>
      <button class="hex-legend-clear" @click="clearPassSelection">{{ $t('hexLegend.clear') }}</button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useFilterStore } from '@/composables/useFilterStore';
import { useMonitoredAreasStore } from '@/composables/useMonitoredAreasStore';
import { useDateTimeSettings } from '@/composables/useDateTimeSettings';
import { useScreenshotMode } from '@/composables/useScreenshotMode';
import { getSatelliteDisplayName } from '@/composables/useConstellationGrouping';
import {
  useSatelliteLatency,
  computeExpectedDeliveryForPass,
  getExpectedLatencyMinutesForPass,
} from '@/composables/useSatelliteLatency';
import {
  getActiveTimingBuckets,
  TIMING_BOUNDARY_TICKS,
  TIMING_WINDOW_HOURS,
} from '@/composables/useTimingColorScale';
import { isColorblindFriendly } from '@/composables/useAccessibilitySettings';

const {
  mapViewMode,
  selectedHexCells,
  hexGridVisible,
  overpassLayerVisible,
  aoiCoverageVisible,
  selectedPassId,
  selectedPass,
  hexTimingSelectedPassesAll,
  ororaConstellationExpanded,
  gridCoverage,
  gridCoverageGaps,
} = useFilterStore();
const { aoiPassesAll } = useMonitoredAreasStore();
const { t } = useI18n();
const { formatDateTime } = useDateTimeSettings();
const { screenshotMode } = useScreenshotMode();
useSatelliteLatency();

function toLegendRgba(rgb: readonly number[], alpha: number): string {
  return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`;
}

const stops = computed(() => {
  const buckets = getActiveTimingBuckets();
  const accessibleAlpha = isColorblindFriendly.value ? 1 : null;
  return buckets.map((bucket, index) => ({
    color: toLegendRgba(
      bucket.rgb,
      accessibleAlpha ?? (index === buckets.length - 1 ? 0.6 : 0.65),
    ),
  }));
});

const boundaryTicks = TIMING_BOUNDARY_TICKS;
/**
 * "No overpasses in the next N hours" is only true inside the window the pipeline
 * actually built. Outside it the grey cells mean the opposite of what that says —
 * there are overpasses, they were just never computed — so the label switches
 * rather than asserting something false.
 */
const unavailableLabel = computed(() => (
  mapViewMode.value === 'grid' && gridCoverageGaps.value.any
    ? t('hexLegend.notComputed')
    : t('hexLegend.noOverpasses', { hours: TIMING_WINDOW_HOURS })
));

/**
 * Why part of the selected range is empty, most limiting reason first.
 *
 * Only shown when the range actually reaches outside what was built, so a range
 * the grid fully covers says nothing at all.
 */
const gridCoverageNotice = computed<string | null>(() => {
  if (mapViewMode.value !== 'grid') return null;
  const coverage = gridCoverage.value;
  const gaps = gridCoverageGaps.value;
  if (!coverage || !gaps.any) return null;
  if (gaps.past) return t('hexLegend.coveragePast');
  if (gaps.beyondAll) {
    return t('hexLegend.coverageBeyondAll', { date: formatDateTime(new Date(coverage.endMs).toISOString()) });
  }
  return t('hexLegend.coverageOtOnly', {
    date: formatDateTime(new Date(coverage.allSatellitesEndMs).toISOString()),
  });
});
const legendGridTemplate = [
  ...Array.from({ length: Math.max(0, stops.value.length - 1) }, () => 'minmax(0, 1fr)'),
  'minmax(0, 1.3fr)',
].join(' ');

const selectedCount = computed(() => selectedHexCells.value.size);
const legendVisible = computed(() => {
  if (mapViewMode.value === 'grid') return hexGridVisible.value;
  if (mapViewMode.value === 'swath') return overpassLayerVisible.value;
  if (mapViewMode.value === 'aoi') return aoiCoverageVisible.value;
  return false;
});

const userCollapsed = ref<boolean | null>(null);
const autoCollapsed = ref(false);
const isCollapsed = computed(() => screenshotMode.value ? false : (userCollapsed.value ?? autoCollapsed.value));
const stackWithTimeFilter = ref(false);
let resizeObserver: ResizeObserver | null = null;
let mapElement: HTMLElement | null = null;
const LEGEND_EXPANDED_WIDTH = 192;
const LEGEND_COLLAPSED_WIDTH = 192;

const selectedLegendPass = computed(() => {
  const id = selectedPassId.value;
  if (!id) return null;
  if (selectedPass.value?.id === id) return selectedPass.value;
  if (mapViewMode.value === 'grid') {
    return hexTimingSelectedPassesAll.value.find((pass) => pass.id === id) ?? null;
  }
  if (mapViewMode.value === 'aoi') {
    return aoiPassesAll.value.find((pass) => pass.id === id) ?? null;
  }
  return selectedPass.value;
});

function clearSelection() {
  selectedHexCells.value = new Set();
}

const selectedPassLabel = computed(() => {
  if (selectedLegendPass.value) {
    return getSatelliteDisplayName(selectedLegendPass.value.name, ororaConstellationExpanded.value);
  }
  const id = selectedPassId.value;
  if (id) {
    const aoiMatch = aoiPassesAll.value.find((p) => p.id === id);
    if (aoiMatch) return getSatelliteDisplayName(aoiMatch.name, ororaConstellationExpanded.value);
  }
  return 'Selected';
});

const selectedPassCloudLabel = computed(() => {
  const cloudCover = selectedLegendPass.value?.cloud_cover_pct;
  const value = cloudCover == null ? t('popup.notCalculated') : `${cloudCover}%`;
  return `${t('common.cloudCoverForecast')}: ${value}`;
});
const selectedPassLatency = computed(() => {
  const pass = selectedLegendPass.value;
  if (!pass) return null;
  return getExpectedLatencyMinutesForPass(pass);
});
const selectedPassDelivery = computed(() => {
  const pass = selectedLegendPass.value;
  if (!pass) return null;
  return computeExpectedDeliveryForPass(pass);
});

function clearPassSelection() {
  selectedPassId.value = null;
}

function toggleCollapsed() {
  userCollapsed.value = !isCollapsed.value;
}

function getTickStyle(index: number): Record<string, string> {
  const lastIndex = boundaryTicks.length - 1;
  if (index === 0) {
    return {
      gridColumn: '1',
      justifySelf: 'start',
      transform: 'none',
    };
  }
  if (index === lastIndex) {
    return {
      gridColumn: String(stops.value.length),
      justifySelf: 'end',
      transform: 'none',
    };
  }
  return {
    gridColumn: String(Math.min(index + 1, stops.value.length)),
    justifySelf: 'start',
    transform: 'translateX(-50%)',
  };
}

onMounted(() => {
  mapElement = document.getElementById('demo-map');
  if (!mapElement) return;

  const updateAutoCollapsed = () => {
    // Collapse automatically when the map viewport is too narrow.
    const width = mapElement?.clientWidth ?? 0;
    if (width <= 0) return;
    autoCollapsed.value = width < 760;
    // Stack only when legend (left-bottom) and time filter (right-bottom) would overlap.
    const legendWidth = isCollapsed.value ? LEGEND_COLLAPSED_WIDTH : LEGEND_EXPANDED_WIDTH;
    const legendLeft = 30;
    const controlSize = 30; // --mk-control-button-size
    const controlSpacing = 8; // --mk-control-spacing (0.5rem)
    const timeFilterWidth = width <= 1100 ? 245 : 265;
    const timeFilterRightInset = controlSize + controlSpacing + (width <= 1100 ? 6.4 : 8.8);
    const timeFilterLeft = width - timeFilterRightInset - timeFilterWidth;
    const paddingGap = 80; // Ensure plenty of clear space before stacking
    stackWithTimeFilter.value = (legendLeft + legendWidth + paddingGap) >= timeFilterLeft;
  };

  updateAutoCollapsed();
  requestAnimationFrame(updateAutoCollapsed);
  resizeObserver = new ResizeObserver(updateAutoCollapsed);
  resizeObserver.observe(mapElement);
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  resizeObserver = null;
  mapElement = null;
});

watch(isCollapsed, () => {
  const width = mapElement?.clientWidth ?? 0;
  if (width <= 0) return;
  const legendWidth = isCollapsed.value ? LEGEND_COLLAPSED_WIDTH : LEGEND_EXPANDED_WIDTH;
  const legendLeft = 30;
  const controlSize = 30;
  const controlSpacing = 8;
  const timeFilterWidth = width <= 1100 ? 245 : 265;
  const timeFilterRightInset = controlSize + controlSpacing + (width <= 1100 ? 6.4 : 8.8);
  const timeFilterLeft = width - timeFilterRightInset - timeFilterWidth;
  const overlapGap = 20;
  stackWithTimeFilter.value = (legendLeft + legendWidth + overlapGap) >= timeFilterLeft;
});
</script>

<style lang="scss">
.hex-legend {
  position: absolute;
  bottom: 32px;
  left: 30px;
  z-index: 100;

  .screenshot-active & {
    bottom: 76px;
  }
  background: rgba(12, 35, 43, 0.92);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.25);
  min-width: 192px;

  &.is-collapsed {
    min-width: 192px;
    padding-bottom: 6px;
  }

  &.is-stacked {
    left: auto;
    right: calc(var(--mk-control-button-size) + var(--mk-control-spacing) + 0.55rem);
    bottom: calc(var(--mk-footer-height) + var(--mk-control-spacing) + 125px);
  }

  &-toggle {
    position: absolute;
    top: 4px;
    right: 4px;
    width: 16px;
    height: 16px;
    border-radius: 3px;
    border: 1px solid rgba(255, 255, 255, 0.18);
    background: rgba(255, 255, 255, 0.08);
    color: var(--p-text-color, #e2e8f0);
    font-size: 0.68rem;
    line-height: 1;
    cursor: pointer;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  &-scale {
    width: 100%;
  }

  &-title {
    font-size: .85rem;
    font-weight: 600;
    color: var(--p-text-color, #e2e8f0);
    margin-bottom: 6px;
    padding-right: 18px;
    white-space: nowrap;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  &-bar {
    display: grid;
    height: 10px;
    border-radius: 3px;
    overflow: hidden;
    width: 100%;
  }

  &-labels {
    margin-top: 6px;
    font-size: 0.68rem;
    line-height: 1.1;
    color: var(--p-text-color, #e2e8f0);
    display: grid;
    align-items: start;
    overflow: visible;

    .tick {
      min-width: 0;
      white-space: nowrap;
      grid-row: 1;
    }

    .tick.is-first {
      justify-self: start;
    }

    .tick.is-last {
      justify-self: end;
    }
  }

  &-coverage-note {
    margin: 0.4rem 0 0;
    padding-top: 0.4rem;
    border-top: 1px solid rgba(148, 163, 184, 0.25);
    font-size: 0.68rem;
    line-height: 1.35;
    color: rgba(148, 163, 184, 0.95);
  }

  &-unavailable {
    margin-top: 6px;
    padding-top: 6px;
    border-top: 1px solid var(--p-overlay-border-color, rgba(255,255,255,0.08));
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.75rem;
    color: var(--p-text-muted-color, #94a3b8);
  }

  &-unavailable-swatch {
    width: 12px;
    height: 10px;
    background: rgba(120,120,120,0.6);
    border-radius: 2px;
  }

  &-selection {
    margin-top: 6px;
    padding-top: 6px;
    border-top: 1px solid var(--p-overlay-border-color, rgba(255, 255, 255, 0.08));
    font-size: 0.75rem;
    color: var(--p-text-color, #e2e8f0);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  &-selected-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }

  &-selected-copy {
    display: flex;
    flex-direction: column;
    min-width: 0;
    gap: 2px;
  }

  &-selected-cloud {
    color: var(--p-text-muted-color, #94a3b8);
    font-size: 0.68rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &-clear {
    background: none;
    border: 1px solid var(--p-overlay-border-color, rgba(255, 255, 255, 0.15));
    border-radius: 4px;
    color: var(--p-text-muted-color, #94a3b8);
    cursor: pointer;
    font-size: 0.65rem;
    padding: 2px 6px;
    font-family: inherit;
    transition: all 0.2s ease;

    &:hover {
      color: var(--p-text-color, #e2e8f0);
      border-color: var(--p-text-muted-color, #94a3b8);
    }
  }
}

:root:not(.color-scheme-dark) .hex-legend {
  background: rgba(255, 255, 255, 0.97);
  border-color: rgba(15, 23, 42, 0.12);
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.16);

  .hex-legend-title,
  .hex-legend-labels,
  .hex-legend-selection {
    color: #1e293b;
  }

  .hex-legend-unavailable {
    color: #475569;
    border-top-color: rgba(15, 23, 42, 0.12);
  }

  .hex-legend-clear {
    color: #475569;
    border-color: rgba(15, 23, 42, 0.2);

    &:hover {
      color: #0f172a;
      border-color: rgba(15, 23, 42, 0.36);
    }
  }

  .hex-legend-toggle {
    background: #f8fafc;
    color: #334155;
    border-color: rgba(15, 23, 42, 0.2);
  }
}

</style>
